/* eslint-disable no-magic-numbers */
import React, { useState } from 'react';
import { Button } from '@/shared/Button';
import { LoadingIndicator } from '@/shared/LoadingIndicator';
import { Input } from '@/shared/Input';
import { Select } from '@/shared/Select';
import { Autocomplete } from '@/shared/Autocomplete';
import { Switch } from '@/shared/Switch/Switch.component';
import { ColorTilePicker } from '@/shared/ColorPicker/ColorTilePicker.component';
import { normalizeHexColor } from '@/utils/convert/normalizeHexColor';
import { FontName } from '@/components/Map/MapViewer/MapViewer.types';
import { MiriamType } from '@/types/models';
import {
  MAX_LINE_WIDTH,
  MIN_LINE_WIDTH,
} from '@/components/FunctionalArea/Modal/LayerLineFactoryModal/LayerLineFactory.constants';
import { useAppSelector } from '@/redux/hooks/useAppSelector';
import { miramiTypesSelector } from '@/redux/configuration/configuration.selectors';
import {
  elementGlyphStateSelector,
  elementSubmapStateSelector,
} from '@/redux/modal/modal.selector';
import { EditElementSection } from '@/components/FunctionalArea/Modal/EditElementModal/EditElementSection.component';
import { ElementImageForm } from '@/components/FunctionalArea/Modal/EditElementModal/ElementImageForm.component';
import { ElementSubmapForm } from '@/components/FunctionalArea/Modal/EditElementModal/ElementSubmapForm.component';
import {
  ElementAlignHorizontal,
  ElementAlignVertical,
  useElementProperties,
} from '@/components/FunctionalArea/Modal/EditElementModal/utils/useElementProperties';

const HORIZONTAL_ALIGN_OPTIONS = [
  { id: 'LEFT', name: 'Left' },
  { id: 'CENTER', name: 'Center' },
  { id: 'RIGHT', name: 'Right' },
];

const VERTICAL_ALIGN_OPTIONS = [
  { id: 'TOP', name: 'Top' },
  { id: 'MIDDLE', name: 'Middle' },
  { id: 'BOTTOM', name: 'Bottom' },
];

const FONT_NAME_OPTIONS: Array<{ id: FontName; name: string }> = [
  { id: 'ARIAL', name: 'Arial' },
  { id: 'HELVETICA', name: 'Helvetica' },
  { id: 'TIMES_NEW_ROMAN', name: 'Times New Roman' },
  { id: 'COURIER_NEW', name: 'Courier New' },
  { id: 'GEORGIA', name: 'Georgia' },
  { id: 'VERDANA', name: 'Verdana' },
];

const fieldLabelClass = 'text-xs font-medium text-font-500';
const gridClass = 'grid grid-cols-3 gap-x-3 gap-y-4';

export const ElementPropertiesForm: React.FC = () => {
  const {
    isCompartment,
    isSending,
    save,
    clampLineWidth,
    name,
    setName,
    notes,
    setNotes,
    immediateLink,
    setImmediateLink,
    fontSize,
    setFontSize,
    fontName,
    setFontName,
    fontColor,
    setFontColor,
    fillColor,
    setFillColor,
    borderColor,
    setBorderColor,
    nameHorizontalAlign,
    setNameHorizontalAlign,
    nameVerticalAlign,
    setNameVerticalAlign,
    lineWidth,
    setLineWidth,
    innerWidth,
    setInnerWidth,
    outerWidth,
    setOuterWidth,
    annotations,
    z,
    allowUiInteraction,
    handleToggleUiInteraction,
    handleAnnotationChange,
    handleAnnotationBlur,
    handleAnnotationTypeChange,
    handleAddAnnotation,
    handleRemoveAnnotation,
    handleBringToFront,
    handleSendToBack,
  } = useElementProperties();

  const miriamTypes = useAppSelector(miramiTypesSelector);
  const elementGlyphState = useAppSelector(elementGlyphStateSelector);
  const elementSubmapState = useAppSelector(elementSubmapStateSelector);
  const [selectedGlyph, setSelectedGlyph] = useState<number | null>(
    elementGlyphState?.glyph ?? null,
  );
  const [selectedMapModel, setSelectedMapModel] = useState<number | null>(
    elementSubmapState?.submap ?? null,
  );
  // A draft (unpersisted) annotation has a negative id; only one is allowed at a time.
  const hasDraftAnnotation = annotations.some(annotation => annotation.id < 0);

  return (
    <div className="flex flex-col gap-6">
      {isSending && (
        <div className="c-edit-element-modal-loader">
          <LoadingIndicator width={44} height={44} />
        </div>
      )}

      <EditElementSection title="Identity">
        <div className={gridClass}>
          <div className="flex flex-col gap-1">
            <span className={fieldLabelClass}>Name</span>
            <Input
              type="text"
              name="name"
              id="name"
              data-testid="element-name"
              value={name}
              onChange={e => setName(e.target.value)}
              onBlur={() => save()}
              sizeVariant="medium"
              className="text-xs"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className={fieldLabelClass}>Description</span>
            <Input
              type="text"
              name="notes"
              id="notes"
              data-testid="element-notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              onBlur={() => save()}
              sizeVariant="medium"
              className="text-xs"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className={fieldLabelClass}>Immediate link</span>
            <Input
              type="text"
              name="immediateLink"
              id="immediateLink"
              data-testid="immediate-link-name"
              value={immediateLink || ''}
              onChange={e => setImmediateLink(e.target.value)}
              onBlur={() => save()}
              sizeVariant="medium"
              className="text-xs"
            />
          </div>
        </div>
      </EditElementSection>

      <EditElementSection title="Image">
        <ElementImageForm selectedGlyph={selectedGlyph} setSelectedGlyph={setSelectedGlyph} />
      </EditElementSection>

      <EditElementSection title="Submap">
        <ElementSubmapForm
          selectedMapModel={selectedMapModel}
          setSelectedMapModel={setSelectedMapModel}
        />
      </EditElementSection>

      <EditElementSection title="Annotations" defaultExpanded={false}>
        <div className="flex flex-col gap-2">
          {annotations.map((annotation, index) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className="grid grid-cols-[1fr_1fr_auto] items-center gap-2"
            >
              <Autocomplete<MiriamType>
                options={miriamTypes}
                initialValue={miriamTypes.find(type => type.name === annotation.type) || null}
                valueKey="name"
                labelKey="commonName"
                placeholder="Type"
                onChange={selected => handleAnnotationTypeChange(index, selected?.name ?? '')}
              />
              <Input
                type="text"
                placeholder="Resource"
                value={annotation.resource}
                onChange={e => handleAnnotationChange(index, 'resource', e.target.value)}
                onBlur={() => handleAnnotationBlur(index)}
                className="text-xs"
              />
              <Button type="button" onClick={() => handleRemoveAnnotation(index)}>
                Remove
              </Button>
            </div>
          ))}
          <Button
            type="button"
            className="w-fit"
            disabled={hasDraftAnnotation}
            onClick={handleAddAnnotation}
          >
            Add annotation
          </Button>
        </div>
      </EditElementSection>

      <EditElementSection title="Text" defaultExpanded={false}>
        <div className={gridClass}>
          <div className="flex flex-col gap-1">
            <span className={fieldLabelClass}>Font name</span>
            <Select
              options={FONT_NAME_OPTIONS}
              selectedId={fontName || null}
              onChange={selectedId => {
                const value = selectedId as FontName;
                setFontName(value);
                save({ fontName: value });
              }}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className={fieldLabelClass}>Font size</span>
            <Input
              type="number"
              name="font-size"
              id="font-size"
              min={0}
              value={fontSize}
              onChange={e => setFontSize(Number(e.target.value))}
              onBlur={() => save()}
              className="text-xs font-medium text-font-400"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className={fieldLabelClass}>Font color</span>
            <ColorTilePicker
              initialColor={fontColor}
              colorChange={color => {
                const parsedColor = normalizeHexColor(color);
                setFontColor(parsedColor);
                save({ fontColor: parsedColor });
              }}
              placement="left"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className={fieldLabelClass}>Name horizontal align</span>
            <Select
              options={HORIZONTAL_ALIGN_OPTIONS}
              selectedId={nameHorizontalAlign || null}
              onChange={selectedId => {
                const value = selectedId as ElementAlignHorizontal;
                setNameHorizontalAlign(value);
                save({ nameHorizontalAlign: value });
              }}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className={fieldLabelClass}>Name vertical align</span>
            <Select
              options={VERTICAL_ALIGN_OPTIONS}
              selectedId={nameVerticalAlign || null}
              onChange={selectedId => {
                const value = selectedId as ElementAlignVertical;
                setNameVerticalAlign(value);
                save({ nameVerticalAlign: value });
              }}
            />
          </div>
        </div>
      </EditElementSection>

      <EditElementSection title="Shape" defaultExpanded={false}>
        <div className={gridClass}>
          <div className="flex flex-col gap-1">
            <span className={fieldLabelClass}>Fill color</span>
            <ColorTilePicker
              initialColor={fillColor}
              colorChange={color => {
                const parsedColor = normalizeHexColor(color);
                setFillColor(parsedColor);
                save({ fillColor: parsedColor });
              }}
              placement="right"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className={fieldLabelClass}>Border color</span>
            <ColorTilePicker
              initialColor={borderColor}
              colorChange={color => {
                const parsedColor = normalizeHexColor(color);
                setBorderColor(parsedColor);
                save({ borderColor: parsedColor });
              }}
              placement="right"
            />
          </div>
          {isCompartment ? (
            <>
              <div className="flex flex-col gap-1">
                <span className={fieldLabelClass}>Inner border thickness</span>
                <Input
                  type="number"
                  name="inner-width"
                  min={MIN_LINE_WIDTH}
                  max={MAX_LINE_WIDTH}
                  id="inner-width"
                  value={innerWidth}
                  onChange={e => setInnerWidth(clampLineWidth(Number(e.target.value)))}
                  onBlur={() => save()}
                  className="text-xs font-medium text-font-400"
                />
              </div>
              <div className="flex flex-col gap-1">
                <span className={fieldLabelClass}>Outer border thickness</span>
                <Input
                  type="number"
                  name="outer-width"
                  min={MIN_LINE_WIDTH}
                  max={MAX_LINE_WIDTH}
                  id="outer-width"
                  value={outerWidth}
                  onChange={e => setOuterWidth(clampLineWidth(Number(e.target.value)))}
                  onBlur={() => save()}
                  className="text-xs font-medium text-font-400"
                />
              </div>
            </>
          ) : (
            <div className="flex flex-col gap-1">
              <span className={fieldLabelClass}>Border thickness</span>
              <Input
                type="number"
                name="line-width"
                min={MIN_LINE_WIDTH}
                max={MAX_LINE_WIDTH}
                id="line-width"
                value={lineWidth}
                onChange={e => setLineWidth(clampLineWidth(Number(e.target.value)))}
                onBlur={() => save()}
                className="text-xs font-medium text-font-400"
              />
            </div>
          )}
        </div>
      </EditElementSection>

      <EditElementSection title="Diagram properties" defaultExpanded={false}>
        <div className="flex items-center gap-2">
          <span className={fieldLabelClass}>z-index: {z}</span>
          <Button type="button" onClick={handleBringToFront}>
            Bring to front
          </Button>
          <Button type="button" onClick={handleSendToBack}>
            Send to back
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <span className={fieldLabelClass}>Clickable on map</span>
          <Switch
            isChecked={allowUiInteraction}
            onToggle={handleToggleUiInteraction}
            data-testid="allow-ui-interaction-switch"
          />
        </div>
      </EditElementSection>
    </div>
  );
};
